/**
 * Treatment variant for A/B test: "Increase Perceived PK Sub Value"
 * Test ID: money_increase-perceived-pk-sub-value
 * LaunchDarkly: https://app.launchdarkly.com/projects/default/flags/money_increase-perceived-pk-sub-value
 * Ticket: https://lovevery.atlassian.net/browse/MONEY-1511
 * Owner: Felipe Moslavacz
 */
import React, { FC, useCallback, useState, useEffect } from 'react';
import {
  TypeImage,
  TypeImageFields,
  TypeMediaTextTitle,
  TypeProduct,
  TypeVideo,
  TypeVideoEmbedFields,
  TypeVideoFields,
} from '@/core/generated-types';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';
import { HeaderText, Loader, LuvImage, RichText, Button } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import { Document } from '@contentful/rich-text-types';
import { shouldUseYotpoReviews } from '@/utils/helpers';
import { YotpoReviewStars } from '@/components/ui/YotpoReviewStars';
import { CarouselProducts } from '@/components/common/Carousel';
import { Asset, Entry, EntryFields } from 'contentful';
import subscriptionItems from './subscriptionItems';
import Image from 'next/image';
import { TariffPDPMessage } from '@/components/common/Tariff';
import { TypeTariffSettings } from '@/components/common/types';
import { ProductOptionsRadio } from '@/components/common/ProductOptionsAddToCart';
import {
  InventoryStatusCode,
  ShopifyProduct,
  ShopifyVariant,
} from '@/lib/shopify';
import useLocale from '@/hooks/useLocale';
import useFeature from '@/hooks/useFeature';
import { createStandaloneProduct, createSubscriptionProduct } from './utils';
import { useInventoryThreshold } from '@/hooks/useInventoryThreshold';
import AddButton from '@/components/common/InventoryStatus/AddButton';

interface PlayKitDetailsHeaderTreatment {
  product: TypeProduct;
  productDescription?: EntryFields.RichText;
  carouselProductsComponents:
    | (TypeMediaTextTitle | TypeVideo | TypeImage)[]
    | Entry<TypeImageFields | TypeVideoFields | TypeVideoEmbedFields>[];
  pathname: string;
  handleClick: (productSelected?: TypeProduct) => void;
  tariffSettings?: TypeTariffSettings;
}

interface PlayKitBodyInterface {
  aboutHeading?: string;
  shipMessage?: string;
}

// Value to be used for the standalone product threshold - if the inventory is below this value, the button will be shown as out of stock
const STANDALONE_THRESHOLD = 10;

export const PlayKitDetailsBodyTreatment: FC<PlayKitBodyInterface> = ({
  aboutHeading,
  shipMessage,
}) => {
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <HeaderText
          classes="text-xl lg:text-2xl"
          size={HeaderSize.H3}
          text={aboutHeading}
        />
        {shipMessage && (
          <h3
            className="font-bold text-charcoal text-h3 md:text-d-h3"
            role="heading"
            dangerouslySetInnerHTML={{ __html: shipMessage }}
          />
        )}
      </div>
      <ul className="flex flex-col gap-3 text-base lg:text-lg">
        {subscriptionItems.map((item) => {
          return (
            <li key={item.sys.id} className="flex items-center gap-2">
              <LuvImage
                Image={item.fields.image.fields.Image as unknown as Asset}
                altText={item.fields.image.fields.altText}
                className="mx-0"
                width={19}
                height={21}
              />
              <p
                dangerouslySetInnerHTML={{ __html: item.fields.description }}
              ></p>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export const PlayKitDetailsHeaderTreatment: React.FC<
  PlayKitDetailsHeaderTreatment
> = ({
  product,
  productDescription,
  carouselProductsComponents,
  pathname,
  handleClick,
  tariffSettings,
}) => {
  const {
    sys: { id },
  } = product;
  const locale = useLocale();
  const {
    api: { updatePurchaseOption },
  } = useSubFlowV3Data();
  const { title, shipMessage, cta, ageDescription } = product.fields;
  const [productSelected, setProductSelected] = useState(product);
  const isSubscribeAndSave = useFeature('money_enable-subscribe-and-save');
  const [isStandaloneSelected, setIsStandaloneSelected] = useState(false);

  useEffect(() => {
    updatePurchaseOption(product);
  }, [product, updatePurchaseOption]);

  const handleOptionSelected = useCallback(
    (
      product: TypeProduct,
      _shopifyProduct: ShopifyProduct,
      _variant: ShopifyVariant,
    ) => {
      setProductSelected(product);
      updatePurchaseOption(product);
      setIsStandaloneSelected(product.sys.id.includes('standalone'));
    },
    [],
  );

  const translations = product.fields.translations?.fields;
  const subscriptionProduct = createSubscriptionProduct(product);
  const standaloneProduct = createStandaloneProduct(product, locale);
  const subscriptionShopifyProduct = subscriptionProduct.fields.shopifyProduct;
  const standaloneShopifyProduct = standaloneProduct?.fields.shopifyProduct;
  const isStandaloneEnabled =
    isSubscribeAndSave?.toString().toLowerCase() === 'enabled' &&
    standaloneProduct &&
    standaloneShopifyProduct;

  const { inventoryData, isLoading } = useInventoryThreshold({
    productId: standaloneShopifyProduct?.id,
    threshold: STANDALONE_THRESHOLD,
    skip: !isStandaloneEnabled,
  });

  return (
    <div className="lg:flex lg:gap-6">
      <div className="pb-3 -m-6 md:mx-0 lg:p-0 lg:m-0 lg:basis-7/12">
        <CarouselProducts
          name="pdp hero carousel"
          id={id}
          components={carouselProductsComponents}
          key={pathname}
        />
      </div>
      <div className="mt-10 lg:mt-0 lg:basis-5/12">
        <p className="text-base font-bold md:text-lg">{ageDescription}</p>
        <HeaderText text={title} size={HeaderSize.H1} />
        {shouldUseYotpoReviews() && (
          <div className="my-3 md:mb-3">
            <YotpoReviewStars product={product} />
          </div>
        )}
        {productDescription && (
          <div className="text-base md:text-lg mb-4">
            <RichText content={productDescription as Document} />
          </div>
        )}

        {isSubscribeAndSave === undefined && <Loader loading={true} />}

        {isStandaloneEnabled && (
          <>
            {standaloneShopifyProduct && subscriptionShopifyProduct && (
              <ProductOptionsRadio
                products={[subscriptionProduct, standaloneProduct]}
                productSelected={productSelected}
                shopifyProducts={[
                  subscriptionShopifyProduct,
                  standaloneShopifyProduct,
                ]}
                onChange={handleOptionSelected}
              />
            )}
            {productSelected.fields.cta &&
              (isStandaloneSelected && inventoryData?.[0]?.isBelowThreshold ? (
                <AddButton
                  inventoryStatus={InventoryStatusCode.OUT_OF_STOCK}
                  product={product}
                  isLoading={isLoading}
                  isDisabledFromCaller={false}
                  dataTestId="out-of-stock-button"
                />
              ) : (
                <Button
                  type="submit"
                  ctaText={productSelected.fields.cta?.fields.linkText}
                  classes="mt-6 lg:mt-4 mx-0 h-14 lg:h-16"
                  onClick={() => handleClick(productSelected)}
                  testId="main-cta-button"
                  fullWidth
                />
              ))}

            <TariffPDPMessage
              product={productSelected}
              tariffSettings={tariffSettings}
            />

            {translations?.ctaMessage && (
              <div className="flex gap-2 items-start justify-center mt-4">
                <p className="text-sm md:text-base">
                  {translations.ctaMessage}
                </p>
              </div>
            )}
          </>
        )}

        {!isStandaloneEnabled && (
          <div className="col-span-12">
            <div className="border-b-[1px] border-charcoal-opacity-20 mt-1 mb-4 lg:mt-7 lg:mb-6" />
            <PlayKitDetailsBodyTreatment
              // This is hardcoded for the A/B test
              aboutHeading={'The Play Kits include'}
              shipMessage={shipMessage}
            />
            {cta && (
              <Button
                type="submit"
                ctaText={cta.fields.linkText}
                classes="mt-6 lg:mt-4 mx-0 h-14 lg:h-16"
                onClick={() => handleClick()}
                fullWidth
              />
            )}

            <TariffPDPMessage
              product={product}
              tariffSettings={tariffSettings}
            />

            <div className="flex gap-2 items-start justify-center mt-4">
              <Image
                src="https://images.ctfassets.net/0sea1vycfyqy/6RW4n2JlYnbOqYHtD8Yupr/d68b2b60faf61022512b826bb586bb42/Delivered_Box.png"
                alt="Delivered box"
                width={21}
                height={24}
                className="mx-0"
              />
              <p className="text-sm md:text-base">
                {/* This is hardcoded for the A/B test */}
                Cancel any time.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
